import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Link2, FlaskConical } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import Marquee from "../components/Marquee";
import { IMAGES } from "../data/site";
import { PRODUCTS, TREADS, totalVariants } from "../data/products";

const FILTERS = [
  { id: "all", label: "All Products" },
  { id: "tread", label: "Precured Tread Rubber" },
  { id: "bonding", label: "Bonding Gum" },
  { id: "solution", label: "Vulcanising Solution" },
];

const ColdProcessPanel = () => (
  <div className="border border-amber/40 bg-panel/50 p-8 sm:p-12" data-testid="cold-process-panel">
    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Cold Process Focus</p>
    <h2 className="mt-4 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
      One system, three components
    </h2>
    <p className="mt-4 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">
      Our product focus is the cold process: MAXIMUS precured tread rubber, bonding gum and vulcanising
      solution, supplied together so your retread crew gets a matched system every time.
    </p>
    <div className="mt-8 grid gap-5 sm:grid-cols-3">
      {[
        { icon: Layers, title: "MAX Tread", copy: "15 precured patterns for radial & bias casings" },
        { icon: Link2, title: "Bonding Gum", copy: "Tread-to-casing adhesion for the cold process" },
        { icon: FlaskConical, title: "Vulcanising Solution", copy: "Dependable, even cure on every fit" },
      ].map((c, i) => (
        <div key={c.title} className="border border-white/10 bg-ink/60 p-6">
          <c.icon size={22} className="text-signal" />
          <h3 className="mt-4 font-display text-xl font-bold uppercase tracking-tight text-white">
            <span className="text-signal font-mono text-[10px] tracking-[0.3em] block mb-1">0{i + 1}</span>
            {c.title}
          </h3>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.copy}</p>
        </div>
      ))}
    </div>
  </div>
);

const Products = () => {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "all";
  const [filter, setFilter] = useState(initial);

  const filtered = PRODUCTS.filter((p) => filter === "all" || p.category === filter);

  const pick = (id) => {
    setFilter(id);
    setParams(id === "all" ? {} : { category: id }, { replace: true });
  };

  return (
    <>
      <section className="relative pt-32 sm:pt-40 pb-16 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0">
          <img src={IMAGES.stacked} alt="Stacked retreaded tyres" className="h-full w-full object-cover opacity-25" data-testid="products-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
          <div className="absolute inset-0 blueprint-grid" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <Reveal y={16}>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber">Product Catalogue</p>
          </Reveal>
          <MaskedLines
            delay={0.1}
            lines={["THE MAX", { node: <span className="text-outline">RANGE</span> }]}
            className="mt-4 font-display font-black uppercase leading-[0.9] tracking-tight text-[clamp(3rem,8vw,7.5rem)]"
          />
          <Reveal delay={0.5} y={16}>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
              All types of radial & bias precured tread rubber, plus bonding gum and vulcanising solution.
              {" "}{TREADS.length} tread patterns · {totalVariants} size variants — every enquiry answered personally.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee
        items={["Request a Quote", "No Online Checkout", "Direct From The Plant", "Radial & Bias", "Cold Process"]}
        className="mb-16"
      />

      <section className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 pb-24" data-testid="catalogue-section">
        <div className="mb-10 flex flex-wrap gap-2" data-testid="catalogue-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => pick(f.id)}
              data-testid={`product-filter-${f.id}`}
              className={`px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                filter === f.id
                  ? "bg-signal border-signal text-white"
                  : "border-white/15 text-slate-400 hover:border-signal/60 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filter === "all" && (
          <Reveal className="mb-12">
            <ColdProcessPanel />
          </Reveal>
        )}

        <div className="flex items-baseline justify-between mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="catalogue-count">
            Showing {filtered.length} product{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>

        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </motion.div>
      </section>
    </>
  );
};

export default Products;
